import React, { useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { type PictureTypeData } from "@/types/acquirebook";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useApiClient } from "@/hooks/useApiClient";
import { usePictureRequestAmount } from "@/api/usePictureRequestAmount";

const MANUAL_LINK_URL =
  "https://trustart.notion.site/4-1d114754f2df806f9991e47fdee087bd#1d114754f2df81008d8cc6cefc792f78";

interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  pictureCheckOptions: PictureTypeData;
  handleAcquireButtonClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    cancelLoading: () => void
  ) => Promise<void>;
  disableConfirmModalCancelButton?: boolean;
}

export const ConfirmAcquireBookDialog: React.FC<IProps> = (props: IProps) => {
  const {
    open,
    setOpen,
    pictureCheckOptions,
    handleAcquireButtonClick,
    disableConfirmModalCancelButton,
  } = props;

  // feature flagの取得
  const { displayChargePriceInRequestModal } = useFeatureFlags();

  // 不動産登記の金額を取得するフック
  const { apiClient } = useApiClient();
  const { data: pictureRequestAmount } = usePictureRequestAmount(apiClient);

  // 不動産登記の金額が全て取得できたかどうかのフラグ
  const [isLoadedPictureRequestAmount, setIsLoadedPictureRequestAmount] =
    React.useState(false);

  // NOTE: システム的に基本的に金額が取得できないことはない
  useEffect(() => {
    if (
      pictureRequestAmount?.shoyushajiko &&
      pictureRequestAmount?.zenbujiko &&
      pictureRequestAmount?.chisekisokuryozu &&
      pictureRequestAmount?.denshikozu &&
      pictureRequestAmount?.chiekikenzumen &&
      pictureRequestAmount?.tatemonozumen
    ) {
      setIsLoadedPictureRequestAmount(true);
    } else {
      setIsLoadedPictureRequestAmount(false);
    }
  }, [pictureRequestAmount]);

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        setOpen(false);
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"確認画面"}</DialogTitle>
      <DialogContent>
        {displayChargePriceInRequestModal && isLoadedPictureRequestAmount ? (
          <DialogContentText id="alert-dialog-description">
            登記/図面の取得を開始します。
            <br />
            取得件数に対して、1件あたり以下の料金が発生します。
            <br />
            <ul>
              {pictureCheckOptions.SHOYUSHAJIKO.checked && (
                <li>所有者事項：{pictureRequestAmount?.shoyushajiko}円</li>
              )}
              {pictureCheckOptions.ZENBUJIKO.checked && (
                <li>全部事項：{pictureRequestAmount?.zenbujiko}円</li>
              )}
              {pictureCheckOptions.CHISEKISOKURYOZU.checked && (
                <li>地積測量図：{pictureRequestAmount?.chisekisokuryozu}円</li>
              )}
              {pictureCheckOptions.DENSHIKOZU.checked && (
                <li>電子公図：{pictureRequestAmount?.denshikozu}円</li>
              )}
              {pictureCheckOptions.CHIEKIKENZUMEN.checked && (
                <li>地役権図面：{pictureRequestAmount?.chiekikenzumen}円</li>
              )}
              {pictureCheckOptions.TATEMONOZUMEN.checked && (
                <li>
                  建物図面/各階平面図：{pictureRequestAmount?.tatemonozumen}円
                </li>
              )}
            </ul>
            ※取得済登記・リユース登記となる場合は価格が異なります。
            <br />
            詳細は
            <a
              href={MANUAL_LINK_URL}
              target="_blank"
              rel="noreferrer"
              style={{ color: "rgb(25, 118, 210)" }}
            >
              マニュアル
            </a>
            をご確認ください。
          </DialogContentText>
        ) : (
          <DialogContentText id="alert-dialog-description">
            【課金が発生します】登記/図面を取得します。
            <br />※ 画面の切り替わりまでに時間がかかる場合があります
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
        <SubmitButton
          timeout={120000}
          variant={ButtonVariantOption.Contained}
          onClick={(event, cancelLoading) => {
            void handleAcquireButtonClick(event, cancelLoading);
          }}
          label={"取得する"}
        />
        <Button
          variant={ButtonVariantOption.Outlined}
          onClick={() => {
            setOpen(false);
          }}
          disabled={disableConfirmModalCancelButton}
          label={"キャンセル"}
        />
      </DialogActions>
    </Dialog>
  );
};
